import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getRecordingMatches } from "@/lib/api";
import { scopedKeyOf, useScopedKey } from "@/lib/queryClient";
import {
  SLOT_LABELS,
  slotKeyToUrl,
  type MatchSlot,
} from "@/features/matches/matchSlots";
import {
  draftContextKey,
  isCurrentScopedIdentity,
  useScopedIdentityContext,
  type DraftContext,
} from "@/features/coach-review/composerDrafts";
import { ownedScopedRequest } from "./ownedScopedRequest";
import type { CoachingNote, Match } from "@/lib/types";
type KnownMatch = Pick<Match, "id" | "team_id"> & Partial<Pick<Match, "slug">>;
export function SourceRecordingLink({
  note,
  knownMatch,
}: {
  note: CoachingNote;
  knownMatch?: KnownMatch;
}) {
  const owner = useScopedIdentityContext();
  return owner ? (
    <OwnedRecordingLink
      key={draftContextKey(owner)}
      owner={owner}
      note={note}
      knownMatch={knownMatch}
    />
  ) : (
    <Unavailable />
  );
}
function Unavailable() {
  return (
    <p className="text-sm text-ink-muted">Source recording unavailable.</p>
  );
}
function OwnedRecordingLink({
  owner,
  note,
  knownMatch,
}: {
  owner: DraftContext;
  note: CoachingNote;
  knownMatch?: KnownMatch;
}) {
  const client = useQueryClient();
  const matchesKey = scopedKeyOf(owner, "matches");
  const cached = !client.getQueryState(matchesKey)?.error
    ? client
        .getQueryData<Match[]>(matchesKey)
        ?.find((m) => m.id === note.match_id && m.team_id === note.team_id)
    : undefined;
  const canonical = (match: KnownMatch | undefined) =>
    match?.id === note.match_id &&
    match.team_id === note.team_id &&
    typeof match.slug === "string" &&
    match.slug.trim() &&
    match.slug.length <= 200
      ? match
      : undefined;
  const known = isCurrentScopedIdentity(owner)
    ? (canonical(knownMatch) ?? canonical(cached))
    : undefined;
  const queryKey = useScopedKey("feedback-recording-match", {
    teamId: note.team_id,
    matchId: note.match_id,
    actor: owner.actorId,
    club: owner.clubId,
    generation: owner.generation,
  });
  const recording = useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      ownedScopedRequest(owner, () =>
        getRecordingMatches(note.team_id, signal),
      ),
    enabled: !known,
    retry: false,
  });
  const match =
    known ??
    canonical(
      recording.data?.find(
        (m) => m.id === note.match_id && m.team_id === note.team_id,
      ),
    );
  if (!isCurrentScopedIdentity(owner) || recording.error)
    return <Unavailable />;
  if (!known && recording.isPending)
    return (
      <p role="status" className="text-sm text-ink-muted">
        Loading source recording…
      </p>
    );
  if (
    !match ||
    !note.slot ||
    !(note.slot in SLOT_LABELS) ||
    note.timestamp_seconds === null ||
    !Number.isFinite(note.timestamp_seconds) ||
    note.timestamp_seconds < 0
  )
    return <Unavailable />;
  const seconds = note.timestamp_seconds;
  return (
    <a
      data-testid="observation-source-recording"
      className="text-sm text-accent underline focus-visible:ring-2 focus-visible:ring-accent"
      href={`/match/${encodeURIComponent(match.slug!)}/${slotKeyToUrl(note.slot)}?seek=${seconds}`}
    >
      Source recording · {SLOT_LABELS[note.slot as MatchSlot]} ·{" "}
      {Math.floor(seconds / 60)}:
      {Math.floor(seconds % 60)
        .toString()
        .padStart(2, "0")}
    </a>
  );
}
